// Desktop main shell — the primary logged-in experience.
//
// Layout ("Rooms sidebar + common strip"):
//   ┌ Header: avatar+name · mobile · location switcher · logout ┐
//   ├ COMMON DEVICES strip (always visible for the location)    ┤
//   ├ Rooms sidebar │ selected room's devices │ device panel    ┤
//
// Flow: pick a location → its Common Devices + Rooms load. Pick a room → its
// devices show in the main area. Pick a device (from a room OR the common strip)
// → its controls open in the inline right-side panel, while the common strip and
// rooms stay visible. Common devices reload whenever the location changes.
//
// Pure front-end over existing endpoints — no backend changes.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, List, ListItemButton, ListItemText, ListItemIcon,
  CircularProgress, IconButton, Divider, Grid, Chip, Button, Tooltip, Snackbar, Alert,
} from '@mui/material';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  getRooms, getCommonDevices, getDevicesByRoom, getDevicesByUser,
  updateRoomSortOrder, updateDeviceSortOrder,
} from '../../../core/api/api';
import useRfSensors from '../../../core/hooks/useRfSensors';
import { rfEventRings, ringRfBell } from '../../shared/rfBell';
import { rfType } from '../../../core/devices/rfTypes';
import SecurityBar from '../../shared/SecurityBar';
import RfSensorTile from '../../shared/RfSensorTile';
import RfSensorDialog from '../../shared/RfSensorDialog';
import RfSensorMenu from '../../shared/RfSensorMenu';
import { arrayMove } from '../../../core/utils/reorder';
import { saveDeviceOrder, applyDeviceOrder } from '../../../core/utils/orderCache';
import { sortByOrder } from '../../../core/utils/sortOrder';
import { useApp } from '../../../core/state/AppContext';
import { useHideOffline, useOnlineOnly } from '../../../core/hooks/useHideOffline';
import DeviceControlCard from '../components/DeviceControlCard';
import RoomScenesManager from './scenes/RoomScenesManager';
import UpdateCodeDialog from '../../shared/UpdateCodeDialog';
import WifiFleetDialog from '../../shared/WifiFleetDialog';
import WifiIcon from '@mui/icons-material/Wifi';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ShareQrDialog from '../../shared/ShareQrDialog';
import RoomPresenceBadge from '../../shared/RoomPresenceBadge';
import SmartRoomBar from '../../shared/SmartRoomBar';
import RoomCycleCard from '../../shared/roomCycle/RoomCycleCard';
import useRoomPresence from '../../../core/hooks/useRoomPresence';
import { deviceGridSpan, deviceCardWidth } from '../../../core/devices/deviceMeta';
import { withoutHubs } from '../../../core/devices/hubDevice';

const asArray = (res) => (res && Array.isArray(res.data) ? res.data : []);
const unoOf = (d) => d && (d.UniqueNumber ?? d.uniqueNumber);

// Rooms and devices carry a SortOrder from the backend; respect it everywhere.
// Shared with the mobile shell — the two used to hold separate copies of this
// rule, and only one of them held any copy at all. See core/utils/sortOrder.

export default function DesktopShell() {
  // Locations, selected location and the Common Devices toggle come from the
  // global header/context.
  const {
    userId, locations, locationId, locationsLoading, locationsError, retryLocations,
    showCommon, editOrder, canReorder, canUpdateFirmware,
  } = useApp();
  // Which scope's Update Code dialog is open (null = none). One piece of state
  // covers both the room button here and the location button in the header.
  const [updateScope, setUpdateScope] = useState(null);
  const [wifiScope, setWifiScope] = useState(null);
  // Share (QR): { target, name } of the location or room being shared, or null.
  const [share, setShare] = useState(null);
  const reordering = editOrder && canReorder;
  const [dragRoomIdx, setDragRoomIdx] = useState(null);
  const [dragDevIdx, setDragDevIdx] = useState(null);

  const [commonDevices, setCommonDevices] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loadingLocData, setLoadingLocData] = useState(false);

  const [roomId, setRoomId] = useState(null);
  const [roomDevices, setRoomDevices] = useState([]);
  const [loadingRoom, setLoadingRoom] = useState(false);

  // When the location changes: reload Common Devices + Rooms, clear selections.
  useEffect(() => {
    if (locationId == null) return undefined;
    let alive = true;
    setLoadingLocData(true);
    setRoomId(null);
    setRoomDevices([]);
    (async () => {
      try {
        const [common, rms] = await Promise.all([
          getCommonDevices(userId, locationId).then(withoutHubs).catch(() => null),
          getRooms(userId, locationId).catch(() => null),
        ]);
        if (!alive) return;
        setCommonDevices(sortByOrder(asArray(common)));
        const rooms2 = sortByOrder(asArray(rms));
        setRooms(rooms2);
        // Auto-select the first room (by sort order) so the grid isn't empty.
        setRoomId(rooms2.length ? rooms2[0].RoomID : null);
      } finally {
        if (alive) setLoadingLocData(false);
      }
    })();
    return () => { alive = false; };
  }, [userId, locationId]);

  // ── RF sensors — doors, safe, motion, panic ──────────────────────────────
  // ⚠️ THIS VIEW SHOWED NONE. An RF sensor is an RfDevice row, in no device
  // list, so it has to be asked for separately — the secondary rooms page did,
  // this main view never did. One call for the location, then: RoomID 0 (the
  // whole location — the main door) in a row under the toolbar, and each
  // room's own beside that room's devices. Named + active only.
  // ⚠️ LIVE, LIKE THE PHONE — and it RINGS. This was a one-off fetch: the tiles
  // never moved and the desktop had no bell, because the phone's bell is an FCM
  // push and a browser gets none. useRfSensors listens to the receivers'
  // `RfV(...)`; a sensor with Notify on rings here while the page is open.
  const [rfEditing, setRfEditing] = useState(null);
  const [rfMenu, setRfMenu] = useState(null);   // right-click: { device, x, y }
  const [rfAlert, setRfAlert] = useState(null);
  // The browser refused to play (no click since the page loaded) — say so, and
  // the next click anywhere is the permission it wants.
  const [soundBlocked, setSoundBlocked] = useState(false);
  useEffect(() => {
    if (!soundBlocked) return undefined;
    const ok = () => setSoundBlocked(false);
    window.addEventListener('pointerdown', ok, { once: true });
    window.addEventListener('keydown', ok, { once: true });
    return () => { window.removeEventListener('pointerdown', ok); window.removeEventListener('keydown', ok); };
  }, [soundBlocked]);
  const onRfEvent = useCallback((sensor, meaning, code) => {
    if (!rfEventRings(sensor, code)) return;
    ringRfBell(sensor, meaning, code).then((r) => { if (r === 'blocked') setSoundBlocked(true); });
    const t = rfType(sensor.RfType);
    const where = String(sensor.RoomName || '').trim();
    setRfAlert({
      key: Date.now(),
      severity: t.level === 'alarm' ? 'error' : 'warning',
      text: `${t.icon} ${String(sensor.Name || t.label).trim()}${where ? ` (${where})` : ''} — ${meaning || 'heard'}`,
    });
  }, []);
  const { sensors: rfAll, reload: loadRf } = useRfSensors(null, {
    locationId: locationId == null ? null : locationId,
    viewerUserId: userId,
    onEvent: onRfEvent,
  });
  // The person on an occupied smart room (door + motion sensor) — sidebar + header.
  const { presenceOf, reload: reloadPresence } = useRoomPresence(locationId, { viewerUserId: userId });
  const rfWholeLocation = rfAll.filter((d) => !(Number(d.RoomID) > 0));
  const rfInRoom = rfAll.filter((d) => roomId != null && Number(d.RoomID) === Number(roomId));

  // Load a room's devices.
  //
  // Passing the location is what makes the list complete: the room endpoint
  // drops whole device types and getDevicesByRoom merges them back from the
  // location list (core/devices/roomMerge.js).
  //
  // The user's full list is still pulled here for the one thing the room reply
  // lacks: `ID` (DevicesInstalled.ID, needed to persist a reorder), matched by
  // unique number.
  //
  // ⚠️ NEVER ADD DEVICES FROM IT BY NAME (bug, 2026-10-17). It used to append any
  // device of the user's whose Room + Location NAME matched the selected room —
  // to recover DeviceIDs 3, 19, 21. Names are not unique: Lavesh's "Home / Hall"
  // and Sanjay's "Home / Hall" matched, so Sanjay's Hall fan (910600) appeared in
  // Lavesh's Hall (All locations, and for anyone mapped to two such homes).
  // Those three types are now fetched BY RoomID inside getDevicesByRoom
  // (devicesByRoomAndType) and the All-locations path reads the room unfiltered,
  // so the name match recovered nothing and only did harm.
  const fetchRoomDevices = useCallback(async (rid) => {
    const [byRoom, allRes] = await Promise.all([
      getDevicesByRoom(userId, rid, locationId).then(withoutHubs).catch(() => null),
      getDevicesByUser(userId).catch(() => null),
    ]);
    const idByUno = new Map(asArray(allRes).map((d) => [String(unoOf(d)), d.ID]));
    const roomDevs = asArray(byRoom).map((d) => ({ ...d, ID: d.ID ?? idByUno.get(String(unoOf(d))) }));
    // Backend SortOrder first, then the local reorder override.
    return applyDeviceOrder(userId, rid, sortByOrder(roomDevs), unoOf);
  }, [userId, locationId]);

  // When a room is selected: load its devices.
  useEffect(() => {
    if (roomId == null) { setRoomDevices([]); return undefined; }
    let alive = true;
    setLoadingRoom(true);
    fetchRoomDevices(roomId)
      .then((devs) => { if (alive) setRoomDevices(devs); })
      .finally(() => { if (alive) setLoadingRoom(false); });
    return () => { alive = false; };
  }, [roomId, fetchRoomDevices]);

  // Re-fetch the current room + common devices so the online dots stay live
  // without a page reload — one list call each.
  //
  // ⚠️ THE CADENCE IS THE FIRMWARE'S, NOT A GUESS. This used to say devices
  // refresh their timestamp "every ~30s" and polled at 30s to match; the switch
  // firmware actually posts every 180000 ms (see deviceStatus.ONLINE_WINDOW_MS
  // for the source), so that was six requests per heartbeat, five of them
  // fetching a value that could not have changed. Polling at one minute still
  // shows a state change well inside the ten-minute window.
  useEffect(() => {
    const id = setInterval(async () => {
      if (editOrder) return; // don't reshuffle mid-drag
      try {
        if (roomId != null) {
          const devs = await fetchRoomDevices(roomId);
          if (devs) setRoomDevices(devs);
        }
        if (locationId != null) {
          const common = await getCommonDevices(userId, locationId).then(withoutHubs).catch(() => null);
          if (common) setCommonDevices(sortByOrder(asArray(common)));
        }
      } catch (e) { /* ignore transient poll errors */ }
    }, 60000);
    return () => clearInterval(id);
  }, [userId, roomId, locationId, fetchRoomDevices, editOrder]);

  // A device was unassigned from its ⋮ menu (DeviceCardMenu): re-read now, so
  // its card leaves the room at once instead of at the next minute's poll.
  useEffect(() => {
    const onChanged = async () => {
      try {
        if (roomId != null) {
          const devs = await fetchRoomDevices(roomId);
          if (devs) setRoomDevices(devs);
        }
        if (locationId != null) {
          const common = await getCommonDevices(userId, locationId).then(withoutHubs).catch(() => null);
          if (common) setCommonDevices(sortByOrder(asArray(common)));
        }
      } catch (e) { /* the minute poll will catch up */ }
    };
    window.addEventListener('sli:devices-changed', onChanged);
    return () => window.removeEventListener('sli:devices-changed', onChanged);
  }, [userId, roomId, locationId, fetchRoomDevices]);

  // Persist a new room order (global — Rooms.SortOrder) then reflect it.
  const persistRoomOrder = async (list) => {
    setRooms(list.map((r, i) => ({ ...r, SortOrder: i })));
    await Promise.all(list.map((r, i) => updateRoomSortOrder(r.RoomID, i).catch(() => null)));
  };
  const onDropRoom = (i) => {
    if (dragRoomIdx != null && dragRoomIdx !== i) persistRoomOrder(arrayMove(rooms, dragRoomIdx, i));
    setDragRoomIdx(null);
  };
  const moveRoom = (i, dir) => {
    const j = i + dir;
    if (j >= 0 && j < rooms.length) persistRoomOrder(arrayMove(rooms, i, j));
  };

  // Persist a new device order: cache locally (reliable for every device type,
  // incl. merged-in ones) AND write backend SortOrder where we have the ID.
  const persistDeviceOrder = async (list) => {
    setRoomDevices(list.map((d, i) => ({ ...d, SortOrder: i })));
    saveDeviceOrder(userId, roomId, list.map((d) => unoOf(d)));
    await Promise.all(list.map((d, i) => (d.ID != null
      ? updateDeviceSortOrder(userId, d.ID, i).catch(() => null) : null)));
  };
  const onDropDev = (i) => {
    if (dragDevIdx != null && dragDevIdx !== i) persistDeviceOrder(arrayMove(roomDevices, dragDevIdx, i));
    setDragDevIdx(null);
  };
  const moveDev = (i, dir) => {
    const j = i + dir;
    if (j >= 0 && j < roomDevices.length) persistDeviceOrder(arrayMove(roomDevices, i, j));
  };

  const selectedRoom = rooms.find((r) => String(r.RoomID) === String(roomId));

  // "Hide offline devices" — the same localStorage flag the mobile shell uses.
  //
  // Suspended while reordering: drag-and-drop and the ↑/↓ buttons address
  // devices by their INDEX in this list, so persisting an order computed from a
  // filtered list would drop every hidden device out of the saved order.
  // Read-only here: the one checkbox lives in the header.
  const [hideOffline] = useHideOffline();
  const filterOn = hideOffline && !reordering;
  // ⚠️ KEEP hiddenCount. It used to be dropped here while the room grid below
  // kept its own, so the room said "2 hidden" and the common strip just showed
  // fewer cards — with its header chip reporting the FILTERED count, so nothing
  // on screen suggested anything was missing. A working energy meter that has
  // not beaten in ten minutes then reads as "that device is gone".
  const { devices: shownCommon, hiddenCount: hiddenInCommon } = useOnlineOnly(commonDevices, filterOn);
  const { devices: shownRoomDevices, hiddenCount: hiddenInRoom } = useOnlineOnly(roomDevices, filterOn);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default', overflow: 'hidden' }}>
      {locationsLoading ? (
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      ) : locations.length === 0 ? (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center', alignItems: 'center' }}>
          <Typography color="text.secondary">
            {locationsError
              ? "Couldn't load your locations. Check your connection and try again."
              : 'No locations available for this account.'}
          </Typography>
          {locationsError && (
            <Button variant="contained" onClick={retryLocations}>Retry</Button>
          )}
        </Box>
      ) : (
        <>
          {/* Page toolbar. The offline filter's CHECKBOX is not here — there is
              one, in the header, governing every list in both shells (see
              core/hooks/useHideOffline). What stays is the count, because the
              filter has to say what it took wherever it takes something. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, pt: 0.5 }}>
            {filterOn && hiddenInRoom > 0 && (
              <Tooltip title={'Offline devices are hidden. Untick "Hide offline" in the header to show them.'}>
                <Chip size="small" variant="outlined" color="warning" label={`${hiddenInRoom} hidden`} />
              </Tooltip>
            )}
            {reordering && hideOffline && (
              <Chip size="small" variant="outlined" label="filter paused while reordering" />
            )}
            {/* 🔓/🔒 for the premises, "is anyone inside", the alert grid and
                the nightly auto-lock — ui/shared/SecurityBar. Anyone with access
                to the location may lock it; the server checks. */}
            {locationId != null && (
              <SecurityBar
                locationId={locationId}
                locationName={locations.find((l) => String(l.LocationID) === String(locationId))?.LocationName || ''}
              />
            )}
            {/* The premises' own sensors (RoomID 0) — the main door belongs to
                the whole location. Always shown, as one-line pills in this row. */}
            {rfWholeLocation.map((d) => (
              <RfSensorTile key={`rf-${d.RfDeviceId}`} device={d} onOpen={setRfEditing} onMenu={(dev, x, y) => setRfMenu({ device: dev, x, y })} pill />
            ))}
            {soundBlocked && (
              <Tooltip title="The browser blocks sound until this page is clicked. Click anywhere and sensor alerts will ring.">
                <Chip color="warning" label="🔇 Sound blocked — click to enable" onClick={() => setSoundBlocked(false)} />
              </Tooltip>
            )}
            <Box sx={{ flex: 1 }} />
            {canUpdateFirmware && locationId != null && (
              <Button
                size="small"
                color="warning"
                startIcon={<SystemUpdateAltIcon fontSize="small" />}
                onClick={() => setUpdateScope({
                  type: 'location',
                  id: locationId,
                  name: locations.find((l) => String(l.LocationID) === String(locationId))?.LocationName || 'Location',
                })}
              >
                Update Code (Location)
              </Button>
            )}
            {/* Wi-Fi for the whole site: writes the location's record AND
                reaches every device, live or asleep. Same gate as the firmware
                push, because both restart the devices they reach. */}
            {canUpdateFirmware && locationId != null && (
              <Button
                size="small"
                startIcon={<WifiIcon fontSize="small" />}
                onClick={() => setWifiScope({
                  type: 'location',
                  id: locationId,
                  name: locations.find((l) => String(l.LocationID) === String(locationId))?.LocationName || 'Location',
                })}
              >
                Wi-Fi (Location)
              </Button>
            )}
            {/* The WHOLE location, door locks included — owner / Super Admin
                only, as the server also insists (core/share/shareAccess). */}
            {canUpdateFirmware && locationId != null && (
              <Button
                size="small"
                startIcon={<QrCode2Icon fontSize="small" />}
                onClick={() => setShare({
                  target: { scope: 'location', locationId: Number(locationId) },
                  name: locations.find((l) => String(l.LocationID) === String(locationId))?.LocationName || 'Location',
                })}
              >
                Share (Location)
              </Button>
            )}
          </Box>

          {/* Common Devices strip — shown only when the header toggle is green. */}
          {showCommon && (
            <>
              <CommonStrip
                devices={shownCommon}
                loading={loadingLocData}
                hiddenCount={filterOn ? hiddenInCommon : 0}
              />
              <Divider />
            </>
          )}

          {/* Rooms | room devices | device controls */}
          <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
            {/* Rooms sidebar */}
            <Box sx={{ width: 180, flexShrink: 0, borderRight: 1, borderColor: 'divider', overflowY: 'auto' }}>
              <Typography variant="overline" sx={{ px: 1.5, pt: 0.75, display: 'block', lineHeight: 2 }} color="text.secondary">
                Rooms
              </Typography>
              {loadingLocData ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}><CircularProgress size={22} /></Box>
              ) : rooms.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                  No rooms in this location
                </Typography>
              ) : (
                <List dense>
                  {rooms.map((r, i) => (
                    <ListItemButton
                      key={r.RoomID}
                      selected={String(r.RoomID) === String(roomId)}
                      onClick={() => setRoomId(r.RoomID)}
                      draggable={reordering}
                      onDragStart={reordering ? (e) => { setDragRoomIdx(i); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); } : undefined}
                      onDragOver={reordering ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } : undefined}
                      onDrop={reordering ? (e) => { e.preventDefault(); onDropRoom(i); } : undefined}
                      sx={reordering ? { cursor: 'grab' } : undefined}
                    >
                      <ListItemIcon sx={{ minWidth: 28, color: 'text.primary' }}>
                        {reordering ? <DragIndicatorIcon fontSize="small" /> : <MeetingRoomIcon fontSize="small" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={r.RoomName || `Room ${r.RoomID}`}
                        primaryTypographyProps={{ color: 'text.primary', noWrap: true }}
                      />
                      {!reordering && <RoomPresenceBadge presence={presenceOf(r.RoomID)} tip sx={{ ml: 0.5 }} />}
                      {reordering && (
                        <Box sx={{ display: 'flex' }} onClick={(e) => e.stopPropagation()}>
                          <IconButton size="small" disabled={i === 0} onClick={() => moveRoom(i, -1)}><KeyboardArrowUpIcon fontSize="small" /></IconButton>
                          <IconButton size="small" disabled={i === rooms.length - 1} onClick={() => moveRoom(i, 1)}><KeyboardArrowDownIcon fontSize="small" /></IconButton>
                        </Box>
                      )}
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>

            {/* Selected room's devices */}
            <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', p: 1.25 }}>
              {roomId == null ? (
                <EmptyHint icon={<MeetingRoomIcon />} text="Select a room to see its devices" />
              ) : loadingRoom ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
              ) : (
                <>
                  {/* ONE HEADER ROW (2026-10-13, "compress, nothing repeated"):
                      name · presence · room lock · scenes · sensors, then the
                      room actions as icons. It was five rows — the header, the
                      lock + a caption repeating the sensor tiles, a boxed Scenes
                      strip and a row of sensor cards — above the first device.
                      Wraps rather than overflows on a narrow window. */}
                  <Box sx={{
                    display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 0.75, rowGap: 0.5, mb: 0.75,
                  }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} color="text.primary" noWrap sx={{ lineHeight: 1.3 }}>
                      {selectedRoom?.RoomName || 'Room'}
                    </Typography>
                    <RoomPresenceBadge presence={presenceOf(roomId)} tip />
                    {/* A smart room (door + motion sensor): its lock, night settings. Nothing otherwise. */}
                    <SmartRoomBar
                      compact
                      presence={presenceOf(roomId)}
                      roomId={roomId}
                      roomName={selectedRoom?.RoomName || 'Room'}
                      locationId={locationId}
                      onChanged={reloadPresence}
                    />
                    {/* AC–Fan cycle chip — only in a room with an AC and a fan. */}
                    <RoomCycleCard compact key={`cycle-${roomId}`} roomId={roomId} roomName={selectedRoom?.RoomName || 'Room'} />
                    {/* Per-room Scenes (max 8) — right-click to add; right-click a
                        scene to edit/delete; left-click to run. */}
                    <RoomScenesManager
                      key={roomId}
                      variant="inline"
                      roomId={roomId}
                      roomName={selectedRoom?.RoomName || 'Room'}
                      initialDevices={roomDevices}
                    />
                    {/* This room's RF sensors, one line each. */}
                    {rfInRoom.map((d) => (
                      <RfSensorTile key={`rf-${d.RfDeviceId}`} device={d} onOpen={setRfEditing} onMenu={(dev, x, y) => setRfMenu({ device: dev, x, y })} pill />
                    ))}
                    <Box sx={{ flex: 1 }} />
                    {/* Room-wide firmware push / Wi-Fi — owner / super admin only. */}
                    {canUpdateFirmware && (
                      <Tooltip title="Update Code — every device in this room">
                        <IconButton size="small" color="warning" onClick={() => setUpdateScope({
                          type: 'room', id: roomId, name: selectedRoom?.RoomName || 'Room', locationId,
                        })}
                        >
                          <SystemUpdateAltIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canUpdateFirmware && (
                      <Tooltip title="Wi-Fi — every device in this room">
                        <IconButton size="small" color="primary" onClick={() => setWifiScope({
                          type: 'room', id: roomId, name: selectedRoom?.RoomName || 'Room', locationId,
                        })}
                        >
                          <WifiIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {/* Anyone with the room: a member passes on only the devices
                        they have themselves — the server decides. */}
                    <Tooltip title="Share this room (QR)">
                      <IconButton size="small" color="primary" onClick={() => setShare({
                        target: { scope: 'room', roomId: Number(roomId) },
                        name: selectedRoom?.RoomName || 'Room',
                      })}
                      >
                        <QrCode2Icon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {roomDevices.length === 0 ? (
                    <EmptyHint icon={<DevicesOtherIcon />} text="No devices in this room" />
                  ) : shownRoomDevices.length === 0 ? (
                    <EmptyHint icon={<DevicesOtherIcon />} text="Every device in this room is offline" />
                  ) : (
                    // The index is only ever used by the reorder handlers, and the
                    // filter is suspended while reordering — so it indexes the full
                    // list, exactly as persistDeviceOrder expects.
                    <Grid container spacing={1} alignItems="stretch">
                      {shownRoomDevices.map((d, i) => (
                        <Grid item {...deviceGridSpan(d.DeviceID)} key={unoOf(d)} sx={{ display: 'flex' }}>
                          <Box
                            sx={{ width: '100%', position: 'relative', cursor: reordering ? 'grab' : 'default' }}
                            draggable={reordering}
                            onDragStart={reordering ? (e) => { setDragDevIdx(i); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); } : undefined}
                            onDragOver={reordering ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } : undefined}
                            onDrop={reordering ? (e) => { e.preventDefault(); onDropDev(i); } : undefined}
                          >
                            {reordering && (
                              <Box sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2, bgcolor: 'rgba(0,0,0,0.6)', borderRadius: 1, display: 'flex', alignItems: 'center' }}>
                                <IconButton size="small" sx={{ color: '#fff', p: 0.25 }} disabled={i === 0} onClick={() => moveDev(i, -1)}><KeyboardArrowUpIcon fontSize="small" /></IconButton>
                                <DragIndicatorIcon fontSize="small" sx={{ color: '#fff' }} />
                                <IconButton size="small" sx={{ color: '#fff', p: 0.25 }} disabled={i === roomDevices.length - 1} onClick={() => moveDev(i, 1)}><KeyboardArrowDownIcon fontSize="small" /></IconButton>
                              </Box>
                            )}
                            <Box sx={{ pointerEvents: reordering ? 'none' : 'auto' }}>
                              <DeviceControlCard device={d} />
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </>
              )}
            </Box>
          </Box>
        </>
      )}

      <WifiFleetDialog
        open={!!wifiScope}
        scope={wifiScope}
        onClose={() => setWifiScope(null)}
      />

      {/* Tapping an RF sensor configures it where it is — location and room
          included, so it can be moved from here too. */}
      <RfSensorDialog
        device={rfEditing}
        locationId={Number(locationId) || 0}
        onClose={() => setRfEditing(null)}
        onSaved={loadRf}
      />
      {/* Right-clicking one: every setting, access and unassign. */}
      <RfSensorMenu
        target={rfMenu}
        onClose={() => setRfMenu(null)}
        onSettings={setRfEditing}
        onChanged={loadRf}
      />

      <Snackbar
        key={rfAlert?.key}
        open={!!rfAlert}
        autoHideDuration={8000}
        onClose={(e, reason) => { if (reason !== 'clickaway') setRfAlert(null); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {rfAlert ? (
          <Alert severity={rfAlert.severity} variant="filled" onClose={() => setRfAlert(null)} sx={{ fontWeight: 700 }}>
            {rfAlert.text}
          </Alert>
        ) : <span />}
      </Snackbar>

      <UpdateCodeDialog
        open={!!updateScope}
        scope={updateScope}
        onClose={() => setUpdateScope(null)}
      />

      <ShareQrDialog
        open={!!share}
        onClose={() => setShare(null)}
        target={share?.target}
        name={share?.name}
      />
    </Box>
  );
}

/*
 * ⚠️ IT MUST SAY WHAT IT IS HIDING. The room grid below has always shown an
 * "N hidden" chip; this strip did not, and its count chip reported the FILTERED
 * length — so with "Hide offline devices" ticked, a working energy meter that
 * had not beaten for ten minutes simply was not there, the header agreed there
 * were fewer devices, and nothing on screen hinted at a filter. That is how two
 * live current sensors at Factory came to be reported as missing devices.
 *
 * A meter reports every ~30 s in normal operation, so ten minutes of silence is
 * genuinely worth flagging — the classification was right. Only the silence
 * about it was wrong.
 */
function CommonStrip({ devices, loading, hiddenCount = 0 }) {
  return (
    <Box sx={{ px: 1.5, py: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <GroupWorkIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="overline" color="text.secondary">Common Devices</Typography>
        {!loading && <Chip size="small" label={devices.length} />}
        {!loading && hiddenCount > 0 && (
          <Tooltip title="Offline devices are hidden by the checkbox below. Untick it to see them.">
            <Chip size="small" variant="outlined" color="warning" label={`${hiddenCount} hidden`} />
          </Tooltip>
        )}
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={22} /></Box>
      ) : devices.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {hiddenCount > 0
            // "None here" and "all of them are hidden" are completely different
            // problems, and telling the user the wrong one sends them to check
            // the wiring of a device that is merely filtered.
            ? `${hiddenCount} common device${hiddenCount > 1 ? 's' : ''} hidden — they have not reported recently. Untick "Hide offline devices" to show them.`
            : 'No common devices for this location'}
        </Typography>
      ) : (
        <Box sx={{
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          pb: 0.5,
          // ⚠️ `flex-start`, NOT `stretch`, and that one word WAS the wasted
          // space. Stretch makes every card as tall as the TALLEST — here the
          // energy meter, which carries a chart and a date range — so a door
          // lock with one icon and a gate with six buttons were both padded out
          // to ~380px of nothing. Each card is now its own height.
          alignItems: 'flex-start',
        }}
        >
          {devices.map((d) => {
            // Width follows the PANEL COLUMNS, not the type — a 7+1 needs four
            // columns and a flat 230 px crushed them into each other.
            const w = deviceCardWidth(d.DeviceID);
            return (
              <Box key={unoOf(d)} sx={{ width: w, flexShrink: 0 }}>
                <DeviceControlCard device={d} />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

function EmptyHint({ icon, text }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 8, color: 'text.secondary' }}>
      <Box sx={{ fontSize: 48, display: 'flex' }}>{icon}</Box>
      <Typography color="text.secondary">{text}</Typography>
    </Box>
  );
}
